// Stable hash → [0,1)
const hashToUnit = (s: string) => {
    let h = 5381;
    for (let i = 0; i < s.length; i++) h = ((h << 5) + h) ^ s.charCodeAt(i);
    return ((h >>> 0) % 10000) / 10000;
};

// map big stocks to gentler surge and smaller headroom
function surgeParamsFor(max: number) {
    if (max <= 25) return {k: 1.6, weight: 0.80, headroom: 0.50}; // small: strong surge, big headroom
    if (max <= 100) return {k: 1.45, weight: 0.65, headroom: 0.40};
    if (max <= 300) return {k: 1.30, weight: 0.50, headroom: 0.30};
    if (max <= 1000) return {k: 1.18, weight: 0.40, headroom: 0.25};
    return {k: 1.12, weight: 0.30, headroom: 0.20};   // 1000+: gentle surge
}

function seededGapWeightFor(max: number) {
    if (max <= 25) return 0.82;
    if (max <= 100) return 0.85;
    if (max <= 300) return 0.88;
    if (max <= 1000) return 0.90;
    return 0.92;
}

export function computeSeededTicketsForWish(opts: {
    id: string;
    actual: number;
    max: number;
    startISO?: string;
    endISO?: string;
    nowMs: number;
    timeCurveK?: number; // optional override
}) {
    const {id, actual, max, startISO, endISO, nowMs} = opts;
    if (!max || max <= 10) return Math.min(Math.max(actual, 0), max);

    const safeActual = Math.min(Math.max(actual, 0), max);

    // τ: strictly from start→end (no pre-start seeding)
    const start = startISO ? new Date(startISO).getTime() : NaN;
    const end = endISO ? new Date(endISO).getTime() : NaN;
    let tau = 1;
    if (Number.isFinite(start) && Number.isFinite(end) && end > start) {
        if (nowMs <= start) tau = 0;
        else if (nowMs >= end) tau = 1;
        else tau = (nowMs - start) / (end - start);
    } else if (Number.isFinite(start) && !Number.isFinite(end)) {
        tau = nowMs >= start ? 1 : 0;
    } else if (!Number.isFinite(start) && Number.isFinite(end)) {
        tau = nowMs >= end ? 1 : 0;
    }

    // ---- size-aware late surge, blended & capped ----
    const sp = surgeParamsFor(max);
    const k = opts.timeCurveK ?? sp.k;     // allow explicit override
    const w = sp.weight;                   // how much surge vs linear
    const head = sp.headroom;              // max % over linear

    const tauLinear = tau;
    const tauSurge = tau > 0 && tau < 1 ? Math.pow(tau, k) : tau;   // k>1 → late surge
    const tauBlend = (1 - w) * tauLinear + w * tauSurge;            // keep some linearity
    const tauFinal = Math.min(tauBlend, tauLinear * (1 + head));    // hard cap over linear

    // deterministic target band (keep your bumped ranges if you like)
    const u = hashToUnit(id);
    const range =
        max <= 25 ? [0.25, 0.50] as const :
            max <= 100 ? [0.30, 0.55] as const :
                [0.35, 0.65] as const;

    const targetPct = range[0] + (range[1] - range[0]) * u;
    const targetAtEnd = Math.floor(max * targetPct);
    const targetNow = Math.min(Math.floor(targetAtEnd * tauFinal), max);
    const gap = Math.max(targetNow - safeActual, 0);
    const shown = safeActual + Math.ceil(gap * seededGapWeightFor(max));

    // blend the time-based target with the real count, while staying within bounds
    return Math.min(shown, max);
}
